import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { Note, NoteFolder } from './notesLens';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Reading and writing personal notes.
 *
 * Nothing here filters by user_id, and that is deliberate rather than an
 * oversight: every policy on both tables is `user_id = auth.uid()`, so the
 * database returns one person's rows and no query in this file could widen
 * that. Adding a client-side `.eq('user_id', me)` would read as the thing
 * providing the isolation, and the day somebody removed it nothing would look
 * wrong.
 *
 * user_id IS set on insert, because the policy's WITH CHECK demands it match
 * the caller — a row without it is refused rather than silently misfiled.
 */

// One literal, unwrapped (PostgREST type inference).
// prettier-ignore
const NOTE_COLUMNS = 'id, folder_id, title, content, is_pinned, updated_at';
const FOLDER_COLUMNS = 'id, name, parent_id';

function fail(message: string): never {
  throw new Error(message);
}

export const noteKeys = {
  all: ['notes'] as const,
  notes: () => [...noteKeys.all, 'notes'] as const,
  folders: () => [...noteKeys.all, 'folders'] as const,
};

export async function fetchNotes(client: SupabaseClient): Promise<Note[]> {
  const { data, error } = await client
    .from('user_notes')
    .select(NOTE_COLUMNS)
    .order('updated_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchFolders(client: SupabaseClient): Promise<NoteFolder[]> {
  const { data, error } = await client
    .from('note_folders')
    .select(FOLDER_COLUMNS)
    .order('name', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export function useNotes() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: noteKeys.notes(),
    queryFn: () => fetchNotes(client),
    enabled: access.status === 'approved',
    retry: false,
  });
}

export function useNoteFolders() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: noteKeys.folders(),
    queryFn: () => fetchFolders(client),
    enabled: access.status === 'approved',
    retry: false,
  });
}

function useMe(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useCreateNote() {
  const { client } = useAuth();
  const userId = useMe();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { folderId: string | null }): Promise<Note> => {
      const { data, error } = await client
        .from('user_notes')
        .insert({ user_id: userId, folder_id: input.folderId, title: '', content: '' })
        .select(NOTE_COLUMNS)
        .single();
      if (error) {
        fail(error.message);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.notes() });
    },
  });
}

/**
 * Save an edit.
 *
 * updated_at is never sent: a database trigger maintains it. A column the app
 * has to remember to set is a column one code path will forget, and this one
 * decides the order of the whole middle panel.
 */
export function useUpdateNote() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      content?: string;
      isPinned?: boolean;
      folderId?: string | null;
    }) => {
      // Typed rather than Record<string, unknown>: the generated Update type
      // rejects excess properties, which is the check that catches a column
      // renamed in a migration and missed here.
      const changes: {
        title?: string;
        content?: string;
        is_pinned?: boolean;
        folder_id?: string | null;
      } = {};
      if (input.title !== undefined) changes.title = input.title;
      if (input.content !== undefined) changes.content = input.content;
      if (input.isPinned !== undefined) changes.is_pinned = input.isPinned;
      if (input.folderId !== undefined) changes.folder_id = input.folderId;
      const { error } = await client.from('user_notes').update(changes).eq('id', input.id);
      if (error) {
        fail(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.notes() });
    },
  });
}

export function useDeleteNote() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await client.from('user_notes').delete().eq('id', id);
      if (error) {
        fail(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.notes() });
    },
  });
}

export function useCreateFolder() {
  const { client } = useAuth();
  const userId = useMe();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; parentId: string | null }) => {
      const { error } = await client
        .from('note_folders')
        .insert({ user_id: userId, name: input.name.trim(), parent_id: input.parentId });
      if (error) {
        fail(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.folders() });
    },
  });
}

export function useDeleteFolder() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await client.from('note_folders').delete().eq('id', id);
      if (error) {
        fail(error.message);
      }
    },
    onSuccess: () => {
      // Both: the folder is gone AND its notes are now unfiled (the foreign
      // key is ON DELETE SET NULL, so their folder_id changed underneath us).
      void queryClient.invalidateQueries({ queryKey: noteKeys.all });
    },
  });
}

/**
 * Delete several notes at once.
 *
 * `.in('id', ids)` is one statement — `delete from user_notes where id = any($1)`
 * — rather than a loop of single deletes. That matters for more than speed: a
 * loop that fails halfway leaves the person with some of their selection gone
 * and no way to tell which.
 *
 * There is deliberately no user_id filter. The RLS delete policy is
 * `user_id = auth.uid()`, so ids belonging to somebody else simply do not
 * match — adding a client-side filter would read as the thing providing that
 * safety, and the day it was removed nothing would look wrong.
 */
export function useDeleteNotes() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) {
        // An empty `in` list is a query that matches nothing, but it is also a
        // round trip for a button press that meant nothing.
        return;
      }
      const { error } = await client.from('user_notes').delete().in('id', ids);
      if (error) {
        fail(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.notes() });
    },
  });
}

/**
 * Move several notes into a folder, or out of every folder.
 *
 * A null folder means All Notes — unfiled, not "no change". The trigger
 * user_notes_folder_is_own refuses a folder belonging to somebody else, so a
 * hand-made id cannot file a note into a stranger's structure.
 */
export function useMoveNotes() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; folderId: string | null }) => {
      if (input.ids.length === 0) {
        return;
      }
      const { error } = await client
        .from('user_notes')
        .update({ folder_id: input.folderId })
        .in('id', input.ids);
      if (error) {
        fail(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.notes() });
    },
  });
}

/**
 * Re-parent a folder.
 *
 * The cycle check runs in the database (note_folders_guard_cycle) as well as
 * in the browser before the drop is offered. Both, because the browser guard
 * covers the path somebody remembered to guard and the trigger covers every
 * path there will ever be.
 */
export function useMoveFolder() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; parentId: string | null }) => {
      const { error } = await client
        .from('note_folders')
        .update({ parent_id: input.parentId })
        .eq('id', input.id);
      if (error) {
        fail(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.folders() });
    },
  });
}
